/**
 * Manager Dashboard Page
 * Team activity visibility and developer health metrics (Phase 7)
 *
 * Displays:
 * - Team activity overview (sessions per developer, patterns learned)
 * - Recent commits and PRs (via git tracking)
 * - Developer health metrics (session frequency, pattern adoption)
 * - Alert summary (struggling developers, violations)
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Progress, Alert, Tooltip } from 'flowbite-react';
import {
  HiUserGroup,
  HiCode,
  HiLightBulb,
  HiExclamation,
  HiTrendingUp,
  HiTrendingDown,
  HiClock,
  HiRefresh,
  HiCheck,
  HiChartBar,
  HiCheckCircle,
  HiExclamationCircle,
  HiInformationCircle,
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import {
  getTeamActivity,
  getPatternStats,
  getTeamAlerts,
  acknowledgeAlert,
  TeamActivityResponse,
  PatternStats,
  AlertsSummary,
  DeveloperAlert,
  DeveloperActivity,
} from '../api/manager';
import { analyticsApi, ConvergenceData } from '../api/analytics';

const ManagerDashboard: React.FC = () => {
  const { hasFeature } = useStatus();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [teamActivity, setTeamActivity] = useState<TeamActivityResponse | null>(null);
  const [patternStats, setPatternStats] = useState<PatternStats | null>(null);
  const [alertsData, setAlertsData] = useState<{ summary: AlertsSummary; alerts: DeveloperAlert[] } | null>(null);
  const [convergence, setConvergence] = useState<ConvergenceData | null>(null);

  const loadData = async () => {
    setError(null);
    setTeamActivity(null);
    setPatternStats(null);
    setAlertsData(null);
    setConvergence(null);

    try {
      const [activityResult, patternsResult, alertsResult, convergenceResult] = await Promise.all([
        getTeamActivity(),
        getPatternStats(),
        getTeamAlerts({ acknowledged: false, limit: 10 }),
        analyticsApi.getConvergence('30d'),
      ]);

      if (activityResult.success) {
        setTeamActivity(activityResult.data || null);
      }

      if (patternsResult.success) {
        setPatternStats(patternsResult.data || null);
      }

      if (alertsResult.success) {
        setAlertsData(alertsResult.data || null);
      }

      if (convergenceResult.success) {
        setConvergence(convergenceResult.data || null);
      }

      const sources = [
        ['Team activity', activityResult],
        ['Pattern statistics', patternsResult],
        ['Alerts', alertsResult],
        ['Convergence', convergenceResult],
      ] as const;
      const errors = sources
        .filter(([, result]) => !result.success || !result.data)
        .map(([name, result]) => `${name}: ${result.error || 'No data returned'}`);
      if (errors.length > 0) setError(errors.join('; '));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    const result = await acknowledgeAlert(alertId);
    if (result.success) {
      // Refresh alerts
      const alertsResult = await getTeamAlerts({ acknowledged: false, limit: 10 });
      if (alertsResult.success) {
        setAlertsData(alertsResult.data || null);
      }
    }
  };

  const getStatusColor = (status: DeveloperActivity['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'stale':
        return 'warning';
      case 'very_stale':
        return 'failure';
      case 'no_ai_usage':
        return 'info';
      case 'low_output':
        return 'warning';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: DeveloperActivity['status']) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'stale':
        return 'Stale (7+ days)';
      case 'very_stale':
        return 'Very Stale (14+ days)';
      case 'no_ai_usage':
        return 'No AI Usage';
      case 'low_output':
        return 'Low Output';
      default:
        return status;
    }
  };

  const getAlertIcon = (type: DeveloperAlert['alert_type']) => {
    switch (type) {
      case 'inactivity':
        return <HiClock className="h-5 w-5" />;
      case 'low_output':
        return <HiTrendingDown className="h-5 w-5" />;
      case 'violation':
        return <HiExclamationCircle className="h-5 w-5" />;
      case 'burnout_risk':
        return <HiExclamation className="h-5 w-5" />;
      case 'struggling':
        return <HiInformationCircle className="h-5 w-5" />;
      default:
        return <HiExclamation className="h-5 w-5" />;
    }
  };

  const getAlertColor = (severity: DeveloperAlert['severity']) => {
    switch (severity) {
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  // Feature gate — Professional+ only
  if (!hasFeature('analytics')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <HiChartBar className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Professional Feature</h2>
        <p className="text-gray-500 text-center max-w-md">
          Manager Dashboard is available on Professional and Enterprise plans.
          Upgrade to access team activity, developer health metrics, and alert monitoring.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mindmeld-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Manager Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Team activity and developer health metrics
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <HiRefresh className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <Alert color="failure">
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Developers */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <HiUserGroup className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Developers</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamActivity?.summary.total_developers ?? '—'}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-green-600">
              {teamActivity?.summary.active_developers ?? '—'} active
            </span>
            <span className="text-yellow-600">
              {teamActivity?.summary.stale_developers ?? '—'} stale
            </span>
          </div>
        </Card>

        {/* Average Sessions */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <HiCode className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Sessions (30d)</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamActivity?.summary.avg_sessions_30d ?? '—'}
              </p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Per developer, last 30 days
          </div>
        </Card>

        {/* Average Commits */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <HiTrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Avg Commits (30d)</p>
              <p className="text-2xl font-bold text-gray-900">
                {teamActivity?.summary.avg_commits_30d ?? '—'}
              </p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Per developer, last 30 days
          </div>
        </Card>

        {/* Patterns Learned */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-amber-100">
              <HiLightBulb className="h-6 w-6 text-amber-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Patterns Learned</p>
              <p className="text-2xl font-bold text-gray-900">
                {patternStats?.total_patterns ?? '—'}
              </p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-green-600">
              {patternStats?.validated ?? '—'} validated
            </span>
            <span className="text-blue-600">
              +{patternStats?.patterns_this_week ?? '—'} this week
            </span>
          </div>
        </Card>
      </div>

      {/* Alerts Section */}
      {alertsData?.alerts && alertsData.alerts.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Active Alerts
              {(alertsData.summary?.unacknowledged ?? 0) > 0 && (
                <Badge color="failure" className="ml-2">
                  {alertsData.summary.unacknowledged}
                </Badge>
              )}
            </h3>
            <a href="/alerts" className="text-sm text-mindmeld-primary hover:underline">
              View all alerts
            </a>
          </div>
          <div className="space-y-3">
            {alertsData.alerts.slice(0, 5).map((alert) => (
              <div
                key={alert.alert_id}
                className={`flex items-center justify-between p-3 rounded-lg border ${getAlertColor(alert.severity)}`}
              >
                <div className="flex items-center space-x-3">
                  {getAlertIcon(alert.alert_type)}
                  <div>
                    <p className="font-medium">{alert.message}</p>
                    <p className="text-sm opacity-75">
                      {alert.developer_name || alert.developer_email}
                      {alert.project_name && ` - ${alert.project_name}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs">
                    {new Date(alert.created_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleAcknowledgeAlert(alert.alert_id)}
                    className="p-1 rounded hover:bg-white/50"
                    title="Acknowledge"
                  >
                    <HiCheck className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* No alerts placeholder */}
      {alertsData?.alerts?.length === 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Active Alerts</h3>
          </div>
          <div className="text-center py-8 text-gray-500">
            <HiCheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p>No active alerts</p>
          </div>
        </Card>
      )}

      {/* Developer Activity Table */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Developer Activity</h3>
          <div className="flex items-center space-x-2 text-sm">
            <Badge color="success">Active</Badge>
            <Badge color="warning">Stale</Badge>
            <Badge color="failure">Very Stale</Badge>
          </div>
        </div>

        {teamActivity?.developers && teamActivity.developers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-700 bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Developer</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Sessions (30d)</th>
                  <th className="px-4 py-3 text-right">Commits (30d)</th>
                  <th className="px-4 py-3 text-right">PRs Merged</th>
                  <th className="px-4 py-3 text-right">Conversion</th>
                  <th className="px-4 py-3 text-right">Last Commit</th>
                </tr>
              </thead>
              <tbody>
                {teamActivity.developers.map((dev) => (
                  <tr key={dev.email_address} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {dev.display_name || dev.email_address.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-500">{dev.email_address}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge color={getStatusColor(dev.status)}>
                        {getStatusLabel(dev.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="font-medium">{dev.sessions.last_30_days}</span>
                        <span className="text-xs text-gray-500">
                          ({dev.sessions.last_7_days} / 7d)
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="font-medium">{dev.commits.last_30_days}</span>
                        <span className="text-xs text-gray-500">
                          ({dev.commits.last_7_days} / 7d)
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {dev.prs_merged_30d}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Tooltip content="Session to commit conversion rate">
                        <div className="flex items-center justify-end">
                          <Progress
                            progress={Math.min(dev.session_to_commit_conversion_pct, 100)}
                            size="sm"
                            color={dev.session_to_commit_conversion_pct >= 50 ? 'green' : 'yellow'}
                            className="w-16 mr-2"
                          />
                          <span className="text-xs">
                            {dev.session_to_commit_conversion_pct.toFixed(0)}%
                          </span>
                        </div>
                      </Tooltip>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      {dev.commits.last_commit ? (
                        <div>
                          <p>{new Date(dev.commits.last_commit).toLocaleDateString()}</p>
                          {dev.commits.days_since_commit !== null && dev.commits.days_since_commit > 0 && (
                            <p className="text-xs text-gray-400">
                              {dev.commits.days_since_commit}d ago
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <HiUserGroup className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{teamActivity ? 'No developer data' : 'Developer activity unavailable'}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {teamActivity ? 'Connect repositories and enable tracking to see developer activity.' : 'Refresh to retry loading activity.'}
            </p>
          </div>
        )}
      </Card>

      {/* Pattern Adoption Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pattern Stats */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pattern Learning Progress</h3>
          {patternStats && patternStats.total_patterns > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Provisional</span>
                <div className="flex items-center">
                  <Progress
                    progress={
                      patternStats.total_patterns > 0
                        ? (patternStats.provisional / patternStats.total_patterns) * 100
                        : 0
                    }
                    color="blue"
                    className="w-32 mr-3"
                  />
                  <span className="text-sm font-medium">{patternStats.provisional}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Validated</span>
                <div className="flex items-center">
                  <Progress
                    progress={
                      patternStats.total_patterns > 0
                        ? (patternStats.validated / patternStats.total_patterns) * 100
                        : 0
                    }
                    color="green"
                    className="w-32 mr-3"
                  />
                  <span className="text-sm font-medium">{patternStats.validated}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Reinforced</span>
                <div className="flex items-center">
                  <Progress
                    progress={
                      patternStats.total_patterns > 0
                        ? (patternStats.reinforced / patternStats.total_patterns) * 100
                        : 0
                    }
                    color="purple"
                    className="w-32 mr-3"
                  />
                  <span className="text-sm font-medium">{patternStats.reinforced}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <HiLightBulb className="mx-auto h-10 w-10 text-gray-400 mb-2" />
              <p>No patterns learned yet</p>
              <p className="text-sm">Patterns are learned from Claude Code sessions</p>
            </div>
          )}
        </Card>

        {/* Quick Actions */}
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <a
              href="/reports"
              className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <HiChartBar className="h-5 w-5 text-gray-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">View Executive Reports</p>
                <p className="text-sm text-gray-500">Engineering investment and ROI analysis</p>
              </div>
            </a>
            <a
              href="/users"
              className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <HiUserGroup className="h-5 w-5 text-gray-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Manage Team Members</p>
                <p className="text-sm text-gray-500">Add or remove developers</p>
              </div>
            </a>
            <a
              href="/knowledge"
              className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <HiLightBulb className="h-5 w-5 text-gray-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Curate Knowledge</p>
                <p className="text-sm text-gray-500">Review and promote team patterns</p>
              </div>
            </a>
          </div>
        </Card>
      </div>

      {/* Team Convergence Analytics */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Team Convergence</h3>
          {convergence && (
            <div className="flex items-center space-x-2">
              <Badge color={convergence.trend === 'improving' ? 'success' : convergence.trend === 'declining' ? 'failure' : 'info'}>
                {convergence.trend === 'improving' ? 'Improving' : convergence.trend === 'declining' ? 'Declining' : 'Stable'}
              </Badge>
              <span className="text-2xl font-bold text-gray-900">{convergence.convergence_score}%</span>
            </div>
          )}
        </div>

        {convergence && convergence.standards_in_use > 0 ? (
          <div className="space-y-6">
            {/* Summary */}
            <p className="text-sm text-gray-600">
              {convergence.developer_count} developer{convergence.developer_count !== 1 ? 's' : ''} using{' '}
              {convergence.standards_in_use} standard{convergence.standards_in_use !== 1 ? 's' : ''} in the last 30 days
            </p>

            {/* Standards Adoption */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Standards Adoption Across Team</h4>
              <div className="space-y-2">
                {convergence.by_standard.slice(0, 8).map((s) => (
                  <div key={s.standard_id} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 truncate max-w-[200px]" title={s.standard_name}>
                      {s.standard_name}
                    </span>
                    <div className="flex items-center ml-4">
                      <Progress
                        progress={s.adoption_rate}
                        color={s.adoption_rate >= 80 ? 'green' : s.adoption_rate >= 50 ? 'yellow' : 'red'}
                        className="w-24 mr-2"
                        size="sm"
                      />
                      <span className="text-xs text-gray-500 w-16 text-right">
                        {s.developers_using}/{convergence.developer_count} devs
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Developer Alignment */}
            {convergence.by_developer.length > 1 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Developer Alignment</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {convergence.by_developer.map((dev) => (
                    <div key={dev.email} className="flex items-center p-2 bg-gray-50 rounded-lg">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {dev.display_name || dev.email.split('@')[0]}
                        </p>
                        <p className="text-xs text-gray-500">
                          {dev.unique_standards} standard{dev.unique_standards !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <span className={`ml-2 text-sm font-bold ${dev.alignment_score >= 70 ? 'text-green-600' : dev.alignment_score >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {dev.alignment_score}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trend Sparkline */}
            {convergence.convergence_trend.length > 1 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Weekly Trend</h4>
                <div className="flex items-end space-x-1 h-16">
                  {convergence.convergence_trend.map((point, i) => (
                    <Tooltip key={i} content={`Week of ${new Date(point.week).toLocaleDateString()}: ${point.score}%`}>
                      <div
                        className={`flex-1 rounded-t ${point.score >= 70 ? 'bg-green-400' : point.score >= 40 ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ height: `${Math.max(point.score, 5)}%` }}
                      />
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <HiUserGroup className="mx-auto h-12 w-12 text-gray-400 mb-2" />
            <p>No convergence data yet</p>
            <p className="text-sm">Standards usage data will appear as your team runs Claude Code sessions</p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ManagerDashboard;
